import {
  DefaultButton,
  FontSizes,
  FontWeights,
  ICheckboxStyles,
  IColumn,
  IDetailsRowBaseProps,
  IDetailsRowProps,
  IDropdownStyles,
  IRenderFunction,
  Stack,
  Theme,
  useTheme,
} from '@fluentui/react';
import { AmountColumnTextView } from 'common/components/AmountView/AmountColumnTextView';
import { showCustomToast } from 'common/components/CustomToast/utils';
import {
  HighLightActiveLink,
  HighlightTextView,
} from 'common/components/HighLight';
import { RedBoxIndicator } from 'common/components/RedBoxIndicator';
import { StatusIcon } from 'common/components/StatusIcon';
import {
  ApprovalRequestInput,
  PurchaseOrderFilter,
} from 'common/types/globalTypes';
import { convertToTitleCase } from 'common/utils/convertToTitleCase';
import {
  dateConvertions,
  dateFormat,
  formatDateTime,
} from 'common/utils/dateFormats';
import {
  PurchaseOrderSearch_purchaseOrderSearch_nodes,
  PurchaseOrderSearchVariables,
} from '../__generated__/PurchaseOrderSearch';
import PURCHASE_ORDER_SEARCH from '../PurchaseOrderSearch.graphql';

import { TextField } from '@fluentui/react';
import { TABLE_ROWS } from 'common/constants';
import React from 'react';
import { PurchaseOrderSearchFilterTotalsVariables } from '../__generated__/PurchaseOrderSearchFilterTotals';
import { PurchaseOrderSearchHintsVariables } from '../__generated__/PurchaseOrderSearchHints';
import { toFilterVariable, toOrderByVariable } from '../utils';
import clsx from 'clsx';
import { useCommonStyles } from 'common/styles';
import { useStyles } from './index.styles';
import { AmountTextView } from 'common/components/AmountView/AmountTextView';
import { PurchaseOrderView } from 'purchaseOrder/view';

export type PurchaseOrderItem =
  PurchaseOrderSearch_purchaseOrderSearch_nodes & {
    purchaseOrderType: string | undefined;
    purchaseOrderDate: string | null;
    department: string | null | undefined;
    currency: string | null | undefined;
    status: string | undefined;
    appliedUsedTotals: string | undefined;
    appliedRemainingAmount: string | undefined;
    appliedAmount: string | undefined;
    appliedRetiredAmount: string | undefined;
    appliedOverageAmount: string | undefined;
  };

interface RenderItemColumnProps {
  item?: PurchaseOrderItem;
  index?: number;
  column?: IColumn;
  styles: Record<string, string>;
  globalSearchText: string;
  transactionStampField?: Map<string, string>;
  setTransactionStampField?: React.Dispatch<
    React.SetStateAction<Map<string, string>>
  >;
  isModalView?: boolean;
  setPOId?: React.Dispatch<React.SetStateAction<string | undefined>>;
}

export const renderItemColumn = ({
  item,
  column,
  styles,
  globalSearchText,
  transactionStampField,
  setTransactionStampField,
  isModalView = false,
  setPOId,
}: RenderItemColumnProps) => {
  if (!item) return null;

  const fieldContent = item[
    column?.fieldName as keyof PurchaseOrderItem
  ] as string;
  const _isUrgentApproval = !Boolean(item._urgencyLevel);

  switch (column?.key) {
    case '_urgencyLevel':
      return (
        <RedBoxIndicator
          _isUrgentApproval={_isUrgentApproval}
          itemId={item.id}
        />
      );

    case 'purchaseOrderNumber':
      if (isModalView)
        return (
          <HighLightActiveLink
            highlightText={globalSearchText}
            text={fieldContent}
            preventDefault
            onClick={() => {
              setPOId?.(item.id);
            }}
          />
        );
      return (
        <Stack verticalAlign="center" className={styles.rowContainer}>
          <HighLightActiveLink
            key={item.id}
            to={`/purchase-orders/purchase-order/${item.id}`}
            highlightText={globalSearchText}
            text={fieldContent}
          />
        </Stack>
      );

    case 'controlTotalAmount':
    case '_baseCurrencyAmount':
    case '_spotCurrencyAmount':
    case 'appliedUsedTotals':
    case 'appliedRemainingAmount':
    case 'appliedAmount':
    case 'appliedRetiredAmount':
    case 'appliedOverageAmount':
      return (
        <Stack verticalAlign="center">
          <AmountColumnTextView
            value={fieldContent}
            searchText={globalSearchText ?? ''}
          />
        </Stack>
      );

    case 'status':
      return (
        <Stack
          verticalAlign="center"
          className={styles.onrenderColumnStack}
          horizontal
          tokens={{ childrenGap: 10 }}
        >
          <HighlightTextView
            className={styles.statusType}
            highlightText={globalSearchText}
            text={convertToTitleCase(item.status!)!}
          />
          <StatusIcon
            approval={false}
            iconType={item.statusType!}
            approvalData={item}
          />
        </Stack>
      );

    case 'transactionStamp':
      if (!item._isAccountingEntryStampedComplete) {
        return (
          <Stack
            verticalAlign="center"
            className={styles.onrenderColumnStack}
            style={{ backgroundColor: 'yellow' }}
          >
            <TextField
              underlined
              value={transactionStampField?.get(item.id) || ''}
              onChange={(_, newValue) => {
                if (newValue) {
                  setTransactionStampField?.(
                    new Map(transactionStampField?.set(item.id, newValue))
                  );
                } else {
                  transactionStampField?.delete(item.id);
                  setTransactionStampField?.(new Map(transactionStampField));
                }
              }}
            />
          </Stack>
        );
      }
      return null;

    case 'userExport':
      return (
        <Stack verticalAlign="center" className={styles.rowContainer}>
          {fieldContent && (
            <HighlightTextView
              highlightText={globalSearchText}
              text={item.userExport?.name ?? ''}
            />
          )}
        </Stack>
      );

    default:
      return (
        <Stack verticalAlign="center" className={styles.rowContainer}>
          {fieldContent && (
            <HighlightTextView
              highlightText={globalSearchText}
              text={fieldContent}
            />
          )}
        </Stack>
      );
  }
};

export function transformPOListData(
  nodes?: PurchaseOrderSearch_purchaseOrderSearch_nodes[]
): PurchaseOrderItem[] | undefined {
  return nodes?.map(
    (po) =>
      ({
        ...po,
        purchaseOrderType: po?.purchaseOrderType?.purchaseOrderType,
        purchaseOrderDate: po?.purchaseOrderDate
          ? dateFormat(dateConvertions(po?.purchaseOrderDate))
          : null,
        department: po?.department?.name,
        businessUnit: po?.businessUnit?.name,
        currency: po?.currency?.isoCode,
        _createdDate: po?._createdDate
          ? formatDateTime(po?._createdDate)
          : null,
        status: po?.statusType?.statusType,
        appliedUsedTotals: po._appliedTotals?.usedAmount,
        appliedRemainingAmount: po._appliedTotals?.remainingAmount,
        appliedAmount: po._appliedTotals?.appliedAmount,
        appliedRetiredAmount: po._appliedTotals?.retiredAmount,
        appliedOverageAmount: po._appliedTotals?.overageAmount,
      } as PurchaseOrderItem)
  );
}

export async function handleApprovalRequest(
  requestComment: string | null,
  requiredDate: string | null,
  selectedRows: any[],
  purchaseOrderApprovalCreate: (options: any) => Promise<any>,
  purchaseOrderSearchVariables: any,
  setIsAllRequestChecked: (value: boolean) => void,
  setRequestedIndices: (value: any[]) => void
) {
  const inputVariables: ApprovalRequestInput[] = selectedRows
    .filter((item) => item._isStagedApprovalRequest)
    .map((requestInput) => ({
      entityId: requestInput.id!,
      rowTimestamp: requestInput._rowTimestamp!,
      comments: requestComment,
      requiredDate: requiredDate ? requiredDate : undefined,
    }));

  if (inputVariables.length > 0) {
    const { errors } = await purchaseOrderApprovalCreate({
      variables: {
        input: {
          entityApproval: inputVariables,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: PURCHASE_ORDER_SEARCH,
          variables: purchaseOrderSearchVariables,
        },
      ],
    });

    if (errors?.length) {
      showCustomToast({
        content: errors[0].message,
        appearance: 'error',
      });
    } else {
      showCustomToast({
        content: 'Request sent for approvals',
        appearance: 'success',
      });
      setIsAllRequestChecked(false);
      setRequestedIndices([]);
    }
  }
}

export function buildPOSearchPayloads({
  showMore,
  newHints,
  globalValue,
  data,
  searchTextValue,
  searchHints,
  sortOrderParam,
  SearchFiltersUpdated,
  supplierId,
  filterMultiStatusTypes,
  POListViewOptions,
  filterParams,
}: {
  showMore: boolean;
  newHints: boolean;
  globalValue: boolean;
  data: any;
  searchTextValue: string | null;
  searchHints?: number | null;
  sortOrderParam: any;
  SearchFiltersUpdated: any[];
  supplierId?: string;
  filterMultiStatusTypes?: any;
  POListViewOptions: any;
  filterParams: Record<string, any>;
}) {
  const filter = SearchFiltersUpdated?.length
    ? ({ and: toFilterVariable(SearchFiltersUpdated) } as PurchaseOrderFilter)
    : undefined;

  const searchRequest = searchTextValue
    ? {
        searchText: [searchTextValue ? searchTextValue : ''],
        searchHintId: searchHints,
      }
    : undefined;

  const variables: PurchaseOrderSearchVariables = {
    first: TABLE_ROWS,
    after: showMore ? data?.purchaseOrderSearch?.pageInfo.endCursor : undefined,
    orderBy: toOrderByVariable(sortOrderParam),
    filter,
    searchRequest,
    isLinkSearch: globalValue,
    filterSupplierId: supplierId,
    filterMultiStatusTypes,
    isSigningSearch: POListViewOptions.isSigning,
    isHistorySearch: POListViewOptions.isHistory,
    isSearchAllData: POListViewOptions.isSearchAll,
  };

  const totalFilterVariables: PurchaseOrderSearchFilterTotalsVariables = {
    searchRequest,
    isLinkSearch: globalValue,
    isSigningSearch: POListViewOptions.isSigning,
    isHistorySearch: POListViewOptions.isHistory,
    isSearchAllData: POListViewOptions.isSearchAll,
    ...filterParams,
    filterSupplierId: supplierId,
  };

  const variableHints: PurchaseOrderSearchHintsVariables = {
    searchRequest: { searchText: [searchTextValue || ''] },
    isLinkSearch: globalValue,
    filterSupplierId: supplierId,
    filterMultiStatusTypes,
  };

  return { variables, totalFilterVariables, variableHints, searchRequest };
}

export const dropdownStyles = (theme: Theme): Partial<IDropdownStyles> => ({
  title: {
    border: 0,
    outline: 0,
    borderBottom: `1px solid ${theme.semanticColors.inputBorder}`,
  },
  dropdown: {
    width: 140,
  },
});
 

export function CommonRowRenderer<TItem>({
  props,
  defaultRender,
  isUrgent,
  mapItem,
}: {
  props?: IDetailsRowProps;
  defaultRender?: IRenderFunction<IDetailsRowProps>;
  isUrgent?: boolean;
  mapItem?: (item: TItem) => TItem;
}) {
  const commonStyles = useCommonStyles();
  const styles = useStyles();

  if (!props) return null;

  const item = mapItem ? mapItem(props.item as TItem) : (props.item as TItem);

  const newProps: IDetailsRowProps = {
    ...props,
    item,
    className: clsx(
      styles.rowBaseStyle,
      isUrgent ? commonStyles.urgentRow : styles.rowBaseStyle
    ),
  };

  return <>{defaultRender?.(newProps)}</>;
}

interface FooterAmountRendererProps {
  totalsMap: Record<string, string | null | undefined>;
  className?: string;
}

export function createFooterAmountRenderer({
  totalsMap,
  className,
}: FooterAmountRendererProps): IDetailsRowBaseProps['onRenderItemColumn'] {
  return (_item, _index, column) => {
    if (!column?.key) return null;

    const fieldContent = totalsMap[column.key] ?? '';

    return (
      <AmountTextView
        variant="medium"
        className={className}
        value={fieldContent}
      />
    );
  };
}
